import { DealItemDataConfig, DealQuestion, DealValue, EnumDataTypes } from ".";
import { NumericFormatPipe } from '../pipes'

/**
 * Represents a row of deal values, mapping model value IDs to their corresponding DealValue objects.
 */
export class DealValueYear {

  /**
   * The one based row number for this value in the database.
   * NOTE!: This is not a match to a row position in a grid!   * 
   */
  rowNo: number;

  /**
   * The configuration for this row, if applicable.
   */
  config?: DealQuestion;

  /**
   * A map of values indexed by time segment
   **/
  values: Map<number, DealValue>; // year -> DealValue

  /**
   * The time range for the deal, defaulting to 1 year.
   */
  dealTimeCols: number = 1;

  /**
   * Display format for range numbers
   */
  protected pipeNumeric = new NumericFormatPipe();

  /**
   * Creates a new instance of DealValueYear.
   */
  constructor(rowNo: number, config: DealQuestion) {

    this.rowNo = rowNo;
    this.config = config;

    this.values = new Map<number, DealValue>();
  }

  /**
   * Adds a DealValue to the row, keyed by its model value ID.
   * @param value The DealValue to add.
   */
  public addDealValue(value: DealValue): void {

    this.values.set(value.timeSegment!, value);
  }

  protected getDisplayRowValues(): any[] {

    const result: any[] = [];

    // Sort the values by the key (year/time segment)
    for (let i = 1; i <= this.dealTimeCols; i++) {

      const value: DealValue | undefined = this.values.get(i);

      if (value) {
        let val: any;

        if (value.options && value.options.length > 0) {
          // If options exist, use the first option's value
          val = value.options[0].val;
        }
        else {
          switch (value.dataTypeCode) {
            case EnumDataTypes.Number:
              val = value.nVal;
              break;
            case EnumDataTypes.DateTime:
              val = value.dtVal;
              break;
            default:
              val = value.sVal;
              break;
          }
        }
        result.push(val);
      }
      else {
        // If no value exists for this time segment, push null or empty
        result.push('');
      }
    }

    return result;
  }

  public toRowArray(): any[] {

    const label = this.config?.displayText ?? "";
    const rangeText = this.buildRangeText();
    const hasHelp = !!this.config?.helpComments;
    const isRequired = this.config?.isRequired === true;

    const needsWrap = hasHelp || rangeText !== "" || isRequired;

    let headerHtml: string;

    if (!needsWrap) {
      headerHtml = label;
    }
    else {
      const asteriskSpan = isRequired
        ? `<span class="pb-deal-char-asterisk hot-header-asterisk" style="margin-left:0.2rem;">*</span>`
        : '';

      const rangeSpan = rangeText
        ? `<span class="hot-header-rng">${rangeText}</span>`
        : '';

      const helpIcon = hasHelp
        ? `<i class="fa-solid fa-circle-question pb-help-icon" data-pb-tooltip="${this.config!.helpComments!.replace(/"/g, '&quot;')}"></i>`
        : '';

      // Wrap asterisk + range + help icon so the trio wraps as one block
      // rather than the icon falling to a separate line from the range.
      const trailingGroup = (asteriskSpan || rangeSpan || helpIcon)
        ? `<span class="hot-header-trailing">${rangeSpan ? ' ' + rangeSpan : ''}${helpIcon ? ' ' + helpIcon : ''}${asteriskSpan}</span>`
        : '';

      headerHtml = `<span class="hot-header-with-help">${label}${trailingGroup ? ' ' + trailingGroup : ''}</span>`;
    }

    return [headerHtml, ...this.getDisplayRowValues()];
  }

  /**
   * Builds the units-aware range suffix (without the leading space).
   * Returns empty string when no bounds are set.
   */
  protected buildRangeText(): string {

    const hasMin = this.config?.min != null;
    const hasMax = this.config?.max != null;

    if (!hasMin && !hasMax) {
      return "";
    }

    const hasUnits = this.config?.unitCode != null && this.config.unitCode !== '';
    const locale = navigator.language || 'en-US';

    const fmt = (val?: number | null) => {

      if (val == null)
        return null;

      if (hasUnits) {
        return new Intl.NumberFormat(locale, {
          minimumFractionDigits: val % 1 === 0 ? 0 : 2,
          maximumFractionDigits: 2
        }).format(val);
      }
      return this.pipeNumeric.transform(val, 0, true, null);
    };

    if (hasMin && hasMax)
      return `(${fmt(this.config?.min)}-${fmt(this.config?.max)})`;

    if (hasMax)
      return `(<= ${fmt(this.config?.max)})`;

    return `(>= ${fmt(this.config?.min)})`;
  }
 
  /**
  *  Retrieve the array of DealValue objects for this row.
  *  This does not include the row header, just the values
  */
  public getData(): DealValue[] {

    // This deliberately now sets id to null!
    const result: DealValue[] = Array.from(this.values.values()).map(v => { v.id = null; return v; }) ?? [];

    return result;
  }
}
